/**
 * Phone Input v1.10.46
 *
 * Integra intl-tel-input para campos de teléfono con banderas de país.
 * v1.10.46: Soporte para campo de contacto (spcb/contact_phone)
 *
 * SOLUCIÓN SHADOW INPUT:
 * WC Blocks usa React que controla el valor del input original.
 * Para evitar que React sobrescriba lo que el usuario escribe,
 * creamos un input "shadow" FUERA del control de React.
 * Al escribir, sincronizamos el valor al input original sin esperar al submit.
 *
 * @package SuperPymeCheckoutBlocks
 */
(function() {
    'use strict';

    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Phone]', ...args);

    // Instancias ITI por input ID (del input ORIGINAL, no shadow)
    const itiInstances = {};

    // Flag para saber si el usuario está editando (evitar sobrescribir)
    const userEditingFlags = {};
    const syncTimers = {};

    // Campos de teléfono a inicializar con intl-tel-input
    // v1.10.46: Agregado contact phone como fuente principal
    const PHONE_SELECTORS = [
        '#contact-spcb-contact_phone', // Teléfono en contacto (siempre visible)
        '#shipping-phone', // Mantener por compatibilidad aunque esté oculto
    ];

    const ITI_OPTIONS = {
        initialCountry: 'cl',
        preferredCountries: ['cl', 'ar', 'pe', 'co', 'mx', 'br', 'us'],
        nationalMode: false,
        formatOnDisplay: false,
        autoPlaceholder: 'aggressive',
        containerClass: 'spcb-iti-container',
        dropdownContainer: document.body,
        i18n: {
            searchPlaceholder: 'Buscar país...',
            noResultsMessage: 'Sin resultados',
            selectedCountryAriaLabel: 'País seleccionado',
            countryListAriaLabel: 'Lista de países',
        }
    };

    function isItiAvailable() {
        return typeof window.intlTelInput !== 'undefined';
    }

    /**
     * Establecer valor en input React (compatible con WC Blocks)
     */
    function setReactInputValue(input, value) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;
        nativeInputValueSetter.call(input, value);

        const inputEvent = new InputEvent('input', {
            bubbles: true,
            cancelable: true,
            inputType: 'insertText',
            data: value,
        });
        input.dispatchEvent(inputEvent);
    }

    function setInputValueSilently(input, value) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;
        nativeInputValueSetter.call(input, value);
    }

    function updateWCBlocksStore(inputId, value) {
        // v1.14.14: CRITICAL - Never update store during checkout processing
        // This prevents race conditions when user clicks "Pagar" while typing phone
        // (blur event fires during checkout processing)
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('updateWCBlocksStore blocked - checkout processing');
            return false;
        }

        if (!window.wp || !window.wp.data) {
            log('wp.data not available');
            return false;
        }

        const dispatch = window.wp.data.dispatch;

        try {
            if (inputId === 'shipping-phone') {
                dispatch('wc/store/cart').setShippingAddress({ phone: value });
                log('Updated WC store shipping phone:', value);
                return true;
            } else if (inputId === 'billing-phone') {
                dispatch('wc/store/cart').setBillingAddress({ phone: value });
                log('Updated WC store billing phone:', value);
                return true;
            }
        } catch (e) {
            log('Error updating WC store:', e);
        }

        return false;
    }

    /**
     * Aplicar estilos personalizados al input con ITI
     */
    function applyCustomStyles(input, shadowContainer) {
        const itiWrapper = input.closest('.iti');
        if (!itiWrapper) return;

        const countryContainer = itiWrapper.querySelector('.iti__country-container');
        if (!countryContainer) return;

        const countryWidth = countryContainer.offsetWidth || 75;
        const marginAfterCountry = 12;
        const paddingValue = countryWidth + marginAfterCountry;

        input.style.cssText = `
            padding-left: ${paddingValue}px !important;
            padding-top: 20px !important;
            padding-bottom: 6px !important;
            padding-right: 12px !important;
            height: 48px !important;
            box-sizing: border-box !important;
            width: 100% !important;
            border: 1px solid #8c8f94 !important;
            border-radius: 4px !important;
            font-size: 16px !important;
        `;

        countryContainer.style.cssText = `
            position: absolute !important;
            top: 12px !important;
            bottom: 6px !important;
            left: 0 !important;
            display: flex !important;
            align-items: center !important;
            border-right: 1px solid #d9d9d9 !important;
            padding-right: 4px !important;
        `;

        shadowContainer.style.position = 'relative';
        shadowContainer.style.height = '48px';

        itiWrapper.style.cssText = `
            width: 100% !important;
            height: 48px !important;
        `;

        log('Applied custom styles to shadow input:', input.id);
    }

    /**
     * Mostrar error de validación
     */
    function showPhoneError(input, message) {
        const wrapper = input.closest('.spcb-phone-shadow');
        if (!wrapper) return;

        hidePhoneError(input);

        const errorDiv = document.createElement('div');
        errorDiv.className = 'spcb-phone-error';
        errorDiv.innerHTML = `
            <span style="display: flex; align-items: center; flex-shrink: 0;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M13.299,3.148 L21.933,18.102 C22.51,19.102 21.789,20.352 20.634,20.352 L3.366,20.352 C2.211,20.352 1.49,19.102 2.067,18.102 L10.701,3.148 C11.278,2.148 12.722,2.148 13.299,3.148 Z" fill="#f0ad4e"/>
                    <path d="M12,15 C11.448,15 11,15.448 11,16 C11,16.552 11.448,17 12,17 C12.552,17 13,16.552 13,16 C13,15.448 12.552,15 12,15 Z M11,9 L11,13 C11,13.552 11.448,14 12,14 C12.552,14 13,13.552 13,13 L13,9 C13,8.448 12.552,8 12,8 C11.448,8 11,8.448 11,9 Z" fill="#000"/>
                </svg>
            </span>
            ${message}
        `;
        errorDiv.style.cssText = `
            color: #cc1818;
            font-size: 12px;
            margin-top: 4px;
            display: flex;
            align-items: center;
            gap: 4px;
        `;

        wrapper.appendChild(errorDiv);

        const itiWrapper = input.closest('.iti');
        if (itiWrapper) {
            input.style.borderColor = '#cc1818 !important';
        }
    }

    function hidePhoneError(input) {
        const wrapper = input.closest('.spcb-phone-shadow');
        if (!wrapper) return;

        const existingError = wrapper.querySelector('.spcb-phone-error');
        if (existingError) {
            existingError.remove();
        }

        // Remove has-error class
        wrapper.classList.remove('has-error');

        input.style.borderColor = '#8c8f94 !important';
    }

    /**
     * Validar teléfono (v1.11.0)
     * Incluye validación de campo requerido y efecto shake como WooCommerce
     */
    function validatePhone(shadowInput, iti, options = {}) {
        if (!shadowInput) return true;

        const value = shadowInput.value.trim();
        const wrapper = shadowInput.closest('.spcb-phone-shadow');
        const inputId = shadowInput.id.replace('-shadow', '');

        // Check if field is required (contact phone is required)
        const isRequired = inputId === 'contact-spcb-contact_phone';

        // Check if user has interacted with the field (for blur validation)
        const hasInteracted = options.forceValidation || wrapper?.classList.contains('was-focused');

        // Empty validation for required fields
        if (!value && isRequired && hasInteracted) {
            showPhoneError(shadowInput, 'Por favor, ingresa un teléfono válido');
            addShakeEffect(wrapper);
            return false;
        }

        // If empty but not required or not interacted, just return true
        if (!value) {
            hidePhoneError(shadowInput);
            return true;
        }

        // Format validation with intl-tel-input
        if (iti && typeof iti.isValidNumber === 'function') {
            const isValid = iti.isValidNumber();

            if (!isValid) {
                const countryData = iti.getSelectedCountryData();
                const countryName = countryData?.name || 'el país seleccionado';
                let errorMsg = `Número inválido para ${countryName}`;

                if (typeof iti.getValidationError === 'function') {
                    const errorCode = iti.getValidationError();
                    const errorMessages = {
                        1: 'Código de país inválido',
                        2: 'Número muy corto',
                        3: 'Número muy largo',
                        4: 'Número inválido',
                        5: 'Longitud inválida',
                    };
                    if (errorMessages[errorCode]) {
                        errorMsg = errorMessages[errorCode];
                    }
                }

                showPhoneError(shadowInput, errorMsg);
                addShakeEffect(wrapper);
                return false;
            }
        }

        hidePhoneError(shadowInput);
        return true;
    }

    /**
     * Agregar efecto shake como WooCommerce Blocks (v1.11.0)
     */
    function addShakeEffect(element) {
        if (!element) return;

        // Add has-error class for red border
        element.classList.add('has-error');

        // Add shake animation
        element.classList.add('spcb-shake');

        // Remove shake class after animation
        setTimeout(() => {
            element.classList.remove('spcb-shake');
        }, 600);
    }

    /**
     * Inicializar phone input con enfoque SHADOW
     *
     * Crea un input "shadow" fuera del control de React,
     * oculta el original, y sincroniza al submit.
     */
    function initPhoneInput(originalInput) {
        if (!originalInput || !isItiAvailable()) return null;

        const inputId = originalInput.id;
        const originalWrapper = originalInput.closest('.wc-block-components-text-input');

        if (!originalWrapper) {
            log('No wrapper found for:', inputId);
            return null;
        }

        // Si ya existe shadow, sincronizar valor y retornar
        const existingShadow = document.getElementById(inputId + '-shadow');
        if (existingShadow) {
            const iti = itiInstances[inputId];
            const originalValue = originalInput.value || '';

            // Sincronizar si el valor del original es diferente al shadow
            if (iti && originalValue) {
                const currentShadowValue = iti.getNumber() || existingShadow.value || '';
                if (originalValue !== currentShadowValue) {
                    log('Syncing existing shadow from original:', inputId, originalValue);
                    iti.setNumber(originalValue);
                }
            }

            log('Shadow already exists for:', inputId);
            return iti || null;
        }

        // Destruir instancia anterior si existe
        if (itiInstances[inputId]) {
            try {
                itiInstances[inputId].destroy();
            } catch (e) {
                log('Error destroying previous ITI instance:', e);
            }
            delete itiInstances[inputId];
        }

        // 1. Obtener valor actual y label
        const currentValue = originalInput.value || '';
        const originalLabel = originalWrapper.querySelector('label');
        const labelText = originalLabel ? originalLabel.textContent : 'Teléfono';

        log('Creating shadow input for:', inputId, 'value:', currentValue);

        // 2. Crear contenedor shadow
        const shadowContainer = document.createElement('div');
        shadowContainer.className = 'wc-block-components-text-input spcb-phone-shadow is-active';
        shadowContainer.setAttribute('data-shadow-for', inputId);

        // 3. Crear shadow input
        const shadowInput = document.createElement('input');
        shadowInput.type = 'tel';
        shadowInput.id = inputId + '-shadow';
        shadowInput.autocomplete = 'tel';
        shadowInput.value = currentValue;
        shadowInput.setAttribute('aria-label', labelText);

        // 4. Crear label
        const shadowLabel = document.createElement('label');
        shadowLabel.setAttribute('for', shadowInput.id);
        shadowLabel.textContent = labelText;
        shadowLabel.style.cssText = `
            position: absolute !important;
            top: 4px !important;
            left: 12px !important;
            font-size: 12px !important;
            color: #767676 !important;
            z-index: 3 !important;
            pointer-events: none !important;
        `;

        // 5. Ensamblar shadow container
        shadowContainer.appendChild(shadowInput);
        shadowContainer.appendChild(shadowLabel);

        // 6. Insertar shadow ANTES del wrapper original
        originalWrapper.parentNode.insertBefore(shadowContainer, originalWrapper);

        // 7. OCULTAR wrapper original (React lo controla pero no se ve)
        originalWrapper.style.display = 'none';

        // 8. Inicializar ITI en shadow input
        try {
            const iti = window.intlTelInput(shadowInput, { ...ITI_OPTIONS, utilsScript: null });
            itiInstances[inputId] = iti;

            // 9. Formatear número existente (si hay valor cargado)
            if (currentValue) {
                setTimeout(() => {
                    // setNumber() aplica formato visual al valor
                    iti.setNumber(currentValue);
                    log('Formatted existing number:', currentValue);
                }, 100);
            }

            // 10. Aplicar estilos
            setTimeout(() => applyCustomStyles(shadowInput, shadowContainer), 50);
            setTimeout(() => applyCustomStyles(shadowInput, shadowContainer), 200);

            // 11. Eventos focus/blur para tracking de edición y validación
            shadowInput.addEventListener('focus', () => {
                userEditingFlags[inputId] = true;
                log('User started editing:', inputId);
            });

            shadowInput.addEventListener('blur', () => {
                // Marcar que el usuario interactuó con el campo
                shadowContainer.classList.add('was-focused');

                // Pequeño delay antes de quitar el flag para evitar race conditions
                setTimeout(() => {
                    userEditingFlags[inputId] = false;
                    log('User stopped editing:', inputId);
                }, 200);

                // Validar con flag de que ya interactuó
                setTimeout(() => validatePhone(shadowInput, iti, { forceValidation: false }), 100);

            });

            // También marcar como editando cuando escribe
            shadowInput.addEventListener('input', () => {
                userEditingFlags[inputId] = true;
                if (syncTimers[inputId]) {
                    clearTimeout(syncTimers[inputId]);
                }

                syncTimers[inputId] = setTimeout(() => {
                    if (window.spcbCheckoutLock?.isProcessing?.()) {
                        log('Skip phone sync - checkout processing');
                        return;
                    }

                    const fullNumber = iti.getNumber() || shadowInput.value || '';
                    if (!fullNumber) return;

                    if (originalInput.value !== fullNumber) {
                        setReactInputValue(originalInput, fullNumber);
                        log('Synced phone to original on input:', inputId, fullNumber);
                    }
                }, 150);
            });

            // 12. Marcar como inicializado
            shadowContainer.classList.add('spcb-phone-initialized');

            log('Shadow input created successfully for:', inputId);
            return iti;

        } catch (e) {
            log('Error initializing ITI on shadow input:', e);
            // Restaurar original si falla
            originalWrapper.style.display = '';
            shadowContainer.remove();
            return null;
        }
    }

    /**
     * Sincronizar todos los valores shadow al input original
     * Ahora solo ajusta el DOM (sin disparar eventos) para evitar writes en submit
     *
     * v1.14.8+: Evitamos writes al Store API durante submit.
     */
    function syncAllShadowsToOriginals() {
        PHONE_SELECTORS.forEach(selector => {
            const originalInput = document.querySelector(selector);
            if (!originalInput) return;

            const inputId = originalInput.id;
            const shadowInput = document.getElementById(inputId + '-shadow');
            const iti = itiInstances[inputId];

            if (shadowInput && iti) {
                const fullNumber = iti.getNumber();
                log('Syncing shadow to original:', inputId, fullNumber);

                // Actualizar DOM sin eventos (el store ya se sincroniza en input)
                if (fullNumber) {
                    setInputValueSilently(originalInput, fullNumber);
                }
            }
        });
    }

    /**
     * Validar todos los teléfonos (v1.11.0)
     * Fuerza validación incluso para campos vacíos si son requeridos
     */
    function validateAllPhones() {
        let allValid = true;
        let firstInvalid = null;

        PHONE_SELECTORS.forEach(selector => {
            const originalInput = document.querySelector(selector);
            if (!originalInput) return;

            const inputId = originalInput.id;
            const shadowInput = document.getElementById(inputId + '-shadow');
            const iti = itiInstances[inputId];
            const wrapper = shadowInput?.closest('.spcb-phone-shadow');

            if (shadowInput) {
                // Marcar como interactuado para forzar validación
                if (wrapper) {
                    wrapper.classList.add('was-focused');
                }

                // Forzar validación (incluso si está vacío)
                const isValid = validatePhone(shadowInput, iti, { forceValidation: true });
                if (!isValid && !firstInvalid) {
                    firstInvalid = shadowInput;
                    allValid = false;
                }
            }
        });

        return { valid: allValid, firstInvalid };
    }

    function initAllPhoneInputs() {
        if (!isItiAvailable()) {
            log('intl-tel-input not available yet');
            return;
        }

        PHONE_SELECTORS.forEach(selector => {
            const input = document.querySelector(selector);
            if (input) {
                initPhoneInput(input);
            }
        });
    }

    /**
     * Sincronizar valor del input original al shadow existente
     * @param {string} inputId - ID del input original
     * @param {boolean} force - Forzar sincronización incluso si el usuario está editando
     */
    function syncOriginalToShadow(inputId, force = false) {
        // NO sincronizar si el usuario está editando (a menos que sea forzado)
        if (!force && userEditingFlags[inputId]) {
            log('Skipping sync - user is editing:', inputId);
            return;
        }

        const originalInput = document.getElementById(inputId);
        const shadowInput = document.getElementById(inputId + '-shadow');
        const iti = itiInstances[inputId];

        if (!originalInput || !shadowInput || !iti) return;

        const originalValue = originalInput.value || '';
        if (!originalValue) return;

        const currentShadowValue = iti.getNumber() || shadowInput.value || '';

        // Solo sincronizar si son diferentes
        if (originalValue !== currentShadowValue) {
            log('Syncing original to shadow:', inputId, originalValue);
            iti.setNumber(originalValue);
        }
    }

    /**
     * Observer simple para detectar cuando aparecen los inputs
     * También detecta cuando se expande el formulario (clic en "Editar")
     */
    function setupMutationObserver() {
        let debounceTimer = null;

        const observer = new MutationObserver((mutations) => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                // Solo inicializar inputs nuevos, NO sincronizar existentes aquí
                // (la sincronización solo ocurre al expandir el formulario)
                PHONE_SELECTORS.forEach(selector => {
                    const input = document.querySelector(selector);
                    if (!input) return;

                    const shadowId = input.id + '-shadow';
                    const existingShadow = document.getElementById(shadowId);

                    if (!existingShadow) {
                        // Shadow no existe - crear
                        initPhoneInput(input);
                    }
                    // NO sincronizar aquí - causa que se sobrescriba lo que el usuario escribe
                });
            }, 150);

            // Detectar cambios en aria-expanded (clic en "Editar")
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
                    const target = mutation.target;
                    if (target.classList.contains('wc-block-components-address-card__edit')) {
                        const isExpanded = target.getAttribute('aria-expanded') === 'true';
                        if (isExpanded) {
                            log('Address form expanded - syncing phone values');
                            // Dar tiempo a WC Blocks para poblar los campos
                            setTimeout(() => {
                                PHONE_SELECTORS.forEach(selector => {
                                    const input = document.querySelector(selector);
                                    if (input) syncOriginalToShadow(input.id);
                                });
                            }, 100);
                            setTimeout(() => {
                                PHONE_SELECTORS.forEach(selector => {
                                    const input = document.querySelector(selector);
                                    if (input) syncOriginalToShadow(input.id);
                                });
                            }, 300);
                        }
                    }
                }
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-expanded', 'value']
        });
        log('MutationObserver setup complete');
    }

    /**
     * Interceptar checkout para validar y sincronizar
     */
    function setupCheckoutInterception() {
        document.addEventListener('click', function(e) {
            const button = e.target.closest('.wc-block-components-checkout-place-order-button');
            if (!button) return;

            // VALIDAR
            const result = validateAllPhones();

            if (!result.valid && result.firstInvalid) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                log('Checkout blocked: invalid phone');

                result.firstInvalid.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                setTimeout(() => {
                    result.firstInvalid.focus();
                }, 300);

                return false;
            }
        }, true);

        log('Checkout interception setup complete');
    }

    function init() {
        log('Initializing v1.10.46 (Shadow Input + Contact Phone)...');
        initAllPhoneInputs();

        // Reintentar varias veces por si el DOM no está listo
        [100, 300, 500, 1000, 2000].forEach(delay => {
            setTimeout(initAllPhoneInputs, delay);
        });

        setupMutationObserver();
        setupCheckoutInterception();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // API pública
    window.spcbPhoneInput = {
        init: initAllPhoneInputs,
        validate: validateAllPhones,
        sync: syncAllShadowsToOriginals,
        syncFromOriginal: syncOriginalToShadow,
        instances: itiInstances,
        editingFlags: userEditingFlags,
        version: '1.10.46'
    };

    log('Phone input v1.10.46 (Shadow Input + Contact Phone) ready');

})();
